import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authService, dashboardService } from '@leoclinic/services';
import type { AuthSession, DashboardMetrics } from '@leoclinic/types';

const AUTH_QUERY_KEY = ['auth', 'session'] as const;
const DASHBOARD_QUERY_KEY = ['dashboard', 'metrics'] as const;

interface UseAuthOptions {
  enabled?: boolean;
}

/**
 * Hook para gestão da sessão de autenticação
 */
export function useAuth(options: UseAuthOptions = {}) {
  const { enabled = true } = options;
  const queryClient = useQueryClient();

  const sessionQuery = useQuery<AuthSession | null>({
    queryKey: AUTH_QUERY_KEY,
    queryFn: () => authService.getSession(),
    staleTime: 5 * 60 * 1000,
    retry: false,
    enabled,
  });

  const signInMutation = useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) =>
      authService.signIn(email, password),
    onSuccess: (session) => {
      queryClient.setQueryData(AUTH_QUERY_KEY, session);
    },
  });

  const signUpMutation = useMutation({
    mutationFn: ({ email, password, fullName }: { email: string; password: string; fullName: string }) =>
      authService.signUp(email, password, fullName),
    onSuccess: (session) => {
      queryClient.setQueryData(AUTH_QUERY_KEY, session);
    },
  });

  const signOutMutation = useMutation({
    mutationFn: () => authService.signOut(),
    onSuccess: () => {
      queryClient.setQueryData(AUTH_QUERY_KEY, null);
      queryClient.clear();
    },
  });

  return {
    session: sessionQuery.data ?? null,
    user: sessionQuery.data?.user ?? null,
    clinic: sessionQuery.data?.clinic ?? null,
    isLoading: enabled && sessionQuery.isLoading,
    isAuthenticated: !!sessionQuery.data,
    signIn: signInMutation.mutateAsync,
    signUp: signUpMutation.mutateAsync,
    signOut: signOutMutation.mutateAsync,
    isSigningIn: signInMutation.isPending,
    isSigningUp: signUpMutation.isPending,
    isSigningOut: signOutMutation.isPending,
    signInError: signInMutation.error,
    signUpError: signUpMutation.error,
  };
}

/**
 * Hook para métricas do dashboard
 */
export function useDashboardMetrics(clinicId: string | null, useMock = false) {
  return useQuery<DashboardMetrics>({
    queryKey: [...DASHBOARD_QUERY_KEY, clinicId, useMock],
    queryFn: async () => {
      if (useMock || !clinicId) {
        return dashboardService.getMockMetrics();
      }
      return dashboardService.getMetrics(clinicId);
    },
    enabled: !!clinicId || useMock,
    staleTime: 60 * 1000,
  });
}

export function useWeeklyRevenue(clinicId: string | null, useMock = false) {
  return useQuery({
    queryKey: [...DASHBOARD_QUERY_KEY, 'weekly', clinicId, useMock],
    queryFn: async () => {
      if (useMock || !clinicId) {
        return [
          { day: 'Seg', revenue: 320, appointments: 4 },
          { day: 'Ter', revenue: 450, appointments: 6 },
          { day: 'Qua', revenue: 280, appointments: 3 },
          { day: 'Qui', revenue: 520, appointments: 7 },
          { day: 'Sex', revenue: 680, appointments: 8 },
          { day: 'Sáb', revenue: 390, appointments: 5 },
          { day: 'Dom', revenue: 0, appointments: 0 },
        ];
      }
      return dashboardService.getWeeklyRevenue(clinicId);
    },
    enabled: !!clinicId || useMock,
  });
}

export { AUTH_QUERY_KEY, DASHBOARD_QUERY_KEY };
export { useClients, useClient, useClientHistory, useClientMutations, CLIENTS_KEY } from './clients';
export { useCategories, useCategory, useCategoryMutations, CATEGORIES_KEY } from './categories';
export { useTreatments, useTreatment, useTreatmentMutations, TREATMENTS_KEY } from './treatments';
export {
  useTherapists,
  useTherapist,
  useAvailableProfiles,
  useWorkingHours,
  useBlockedPeriods,
  useTherapistsOnDuty,
  useTherapistMutations,
  THERAPISTS_KEY,
} from './therapists';
export {
  useAgendaEvents,
  useAppointment,
  useAppointmentMutations,
  useAppointmentsList,
  APPOINTMENTS_KEY,
} from './appointments';
export { usePayments, usePaymentMutations, PAYMENTS_KEY } from './payments';
export { useWaitlist, useWaitlistMutations, WAITLIST_KEY } from './waitlist';
export { useReportSummary, useAccountingReport, REPORTS_KEY } from './reports';
export {
  useCampaigns,
  useCampaignMutations,
  useCoupons,
  useCouponMutations,
  CAMPAIGNS_KEY,
  COUPONS_KEY,
} from './marketing';
export { useNotifications, useUnreadCount, useNotificationMutations, NOTIFICATIONS_KEY } from './notifications';
export { useSettings, useSettingsMutations, SETTINGS_KEY } from './settings';
export { useClinicMutations } from './clinic';
export {
  usePublicTreatments,
  usePublicTherapists,
  useOnlineBooking,
  useAvailableSlots,
  useAppointmentSettings,
  usePublicClinic,
  BOOKING_KEY,
} from './booking';
export { useMyClient, useMyAppointments, useClientPortalMutations, CLIENT_PORTAL_KEY } from './client-portal';
export { useClinicalSummaries, useAiMutations, AI_KEY } from './ai';
